import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { floorPlanExtractionActions, BandTemplate } from "../../../reducers/floorPlanExtraction";

interface UseBandTemplateManagementProps {
  bandTemplates: BandTemplate[];
  selectedBandTemplate: BandTemplate | null;
}

interface UseBandTemplateManagementReturn {
  // State
  showNewTemplateForm: boolean;
  setShowNewTemplateForm: (show: boolean) => void;
  showTemplateSelectPopup: boolean;
  setShowTemplateSelectPopup: (show: boolean) => void;
  newTemplateName: string;
  setNewTemplateName: (name: string) => void;
  newTemplatePosition: 'top' | 'bottom';
  setNewTemplatePosition: (position: 'top' | 'bottom') => void;
  newTemplateFile: File | null;
  setNewTemplateFile: (file: File | null) => void;
  newTemplatePreview: string | null;
  setNewTemplatePreview: (preview: string | null) => void;
  isSavingTemplate: boolean;
  editingTemplateId: number | null;
  // 削除確認ポップアップ
  showDeleteConfirmPopup: boolean;
  pendingDeleteTemplateId: number | null;
  // Handlers
  handleTemplateChange: (templateIdStr: string) => void;
  handleNewTemplateFileChange: (file: File) => void;
  resetNewTemplateForm: () => void;
  handleSaveNewTemplate: () => Promise<void>;
  handleRequestDeleteTemplate: (templateId: number) => void;
  handleConfirmDeleteTemplate: () => Promise<void>;
  handleCancelDeleteTemplate: () => void;
  handleOpenEditTemplatePopup: (template: BandTemplate) => void;
  handleUpdateTemplate: () => Promise<void>;
  handleSelectTemplate: (template: BandTemplate) => void;
  // 帯操作
  handlePositionChange: (position: 'top' | 'bottom') => void;
  handleScaleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useBandTemplateManagement = ({
  bandTemplates,
  selectedBandTemplate,
}: UseBandTemplateManagementProps): UseBandTemplateManagementReturn => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  // State
  const [showNewTemplateForm, setShowNewTemplateForm] = useState(false);
  const [showTemplateSelectPopup, setShowTemplateSelectPopup] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplatePosition, setNewTemplatePosition] = useState<'top' | 'bottom'>('bottom');
  const [newTemplateFile, setNewTemplateFile] = useState<File | null>(null);
  const [newTemplatePreview, setNewTemplatePreview] = useState<string | null>(null);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
  // 削除確認ポップアップ
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [pendingDeleteTemplateId, setPendingDeleteTemplateId] = useState<number | null>(null);

  // 帯テンプレートを選択
  const handleTemplateChange = useCallback((templateIdStr: string) => {
    // ローカルテンプレートをクリア
    dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));

    if (!templateIdStr) {
      dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
      return;
    }

    const templateId = parseInt(templateIdStr, 10);
    const template = bandTemplates.find(t => t.id === templateId);
    if (template) {
      dispatch(floorPlanExtractionActions.setSelectedBandTemplate(template));
      // テンプレートに保存されている位置を適用
      dispatch(floorPlanExtractionActions.setBandPosition(template.position));
    } else {
      dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
    }
  }, [dispatch, bandTemplates]);

  // 新規テンプレート用ファイル選択
  const handleNewTemplateFileChange = useCallback((file: File) => {
    setNewTemplateFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewTemplatePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  // 新規テンプレート登録フォームをリセット
  const resetNewTemplateForm = useCallback(() => {
    setShowNewTemplateForm(false);
    setNewTemplateName('');
    setNewTemplatePosition('bottom');
    setNewTemplateFile(null);
    setNewTemplatePreview(null);
    setEditingTemplateId(null);
  }, []);

  // 新規テンプレート保存
  const handleSaveNewTemplate = useCallback(async () => {
    if (!newTemplateFile || !newTemplateName.trim()) return;

    // 重複名チェック
    const isDuplicateName = bandTemplates.some(
      (template) => template.name === newTemplateName.trim()
    );
    if (isDuplicateName) {
      alert('このテンプレート名は既に使用されています。別の名前を入力してください。');
      return;
    }

    setIsSavingTemplate(true);
    try {
      await dispatch(floorPlanExtractionActions.saveBandTemplate({
        name: newTemplateName.trim(),
        image: newTemplateFile,
        position: newTemplatePosition,
      })).unwrap();
      resetNewTemplateForm();
      // テンプレート一覧を再取得
      dispatch(floorPlanExtractionActions.fetchBandTemplates());
    } catch (error) {
      console.error('テンプレート保存エラー:', error);
      alert('テンプレートの保存に失敗しました');
    } finally {
      setIsSavingTemplate(false);
    }
  }, [dispatch, newTemplateFile, newTemplateName, newTemplatePosition, bandTemplates, resetNewTemplateForm]);

  // テンプレート削除リクエスト（ポップアップを表示）
  const handleRequestDeleteTemplate = useCallback((templateId: number) => {
    setPendingDeleteTemplateId(templateId);
    setShowDeleteConfirmPopup(true);
  }, []);

  // テンプレート削除確認
  const handleConfirmDeleteTemplate = useCallback(async () => {
    if (!pendingDeleteTemplateId) return;

    try {
      await dispatch(floorPlanExtractionActions.deleteBandTemplate(pendingDeleteTemplateId)).unwrap();
      // 選択中のテンプレートが削除された場合はクリア
      if (selectedBandTemplate?.id === pendingDeleteTemplateId) {
        dispatch(floorPlanExtractionActions.setSelectedBandTemplate(null));
      }
    } catch (error) {
      console.error('テンプレート削除エラー:', error);
      alert('テンプレートの削除に失敗しました');
    } finally {
      setShowDeleteConfirmPopup(false);
      setPendingDeleteTemplateId(null);
    }
  }, [dispatch, selectedBandTemplate, pendingDeleteTemplateId]);

  // テンプレート削除キャンセル
  const handleCancelDeleteTemplate = useCallback(() => {
    setShowDeleteConfirmPopup(false);
    setPendingDeleteTemplateId(null);
  }, []);

  // 編集ポップアップを開く
  const handleOpenEditTemplatePopup = useCallback((template: BandTemplate) => {
    setNewTemplateName(template.name);
    setNewTemplateFile(null);
    setNewTemplatePreview(template.image_url);
    setNewTemplatePosition(template.position);
    setEditingTemplateId(template.id);
    setShowTemplateSelectPopup(false);
    setShowNewTemplateForm(true);
  }, []);

  // テンプレート更新
  const handleUpdateTemplate = useCallback(async () => {
    if (!editingTemplateId || !newTemplateName.trim()) return;

    // 重複名チェック（自分自身は除外）
    const isDuplicateName = bandTemplates.some(
      (template) => template.name === newTemplateName.trim() && template.id !== editingTemplateId
    );
    if (isDuplicateName) {
      alert('このテンプレート名は既に使用されています。別の名前を入力してください。');
      return;
    }

    setIsSavingTemplate(true);
    try {
      await dispatch(floorPlanExtractionActions.updateBandTemplate({
        id: editingTemplateId,
        name: newTemplateName.trim(),
        position: newTemplatePosition,
      })).unwrap();
      resetNewTemplateForm();
      setEditingTemplateId(null);
      // テンプレート一覧を再取得
      dispatch(floorPlanExtractionActions.fetchBandTemplates());
    } catch (error) {
      console.error('テンプレート更新エラー:', error);
      alert('テンプレートの更新に失敗しました');
    } finally {
      setIsSavingTemplate(false);
    }
  }, [dispatch, editingTemplateId, newTemplateName, newTemplatePosition, bandTemplates, resetNewTemplateForm]);

  // テンプレートをクリック選択
  const handleSelectTemplate = useCallback((template: BandTemplate) => {
    dispatch(floorPlanExtractionActions.setLocalBandTemplate(null));
    dispatch(floorPlanExtractionActions.setSelectedBandTemplate(template));
    dispatch(floorPlanExtractionActions.setBandPosition(template.position));
  }, [dispatch]);

  // 帯位置を変更
  const handlePositionChange = useCallback((position: 'top' | 'bottom') => {
    dispatch(floorPlanExtractionActions.setBandPosition(position));
  }, [dispatch]);

  // スケール変更
  const handleScaleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newScale = parseFloat(e.target.value);
    dispatch(floorPlanExtractionActions.setBandScale(newScale));
  }, [dispatch]);

  return {
    // State
    showNewTemplateForm,
    setShowNewTemplateForm,
    showTemplateSelectPopup,
    setShowTemplateSelectPopup,
    newTemplateName,
    setNewTemplateName,
    newTemplatePosition,
    setNewTemplatePosition,
    newTemplateFile,
    setNewTemplateFile,
    newTemplatePreview,
    setNewTemplatePreview,
    isSavingTemplate,
    editingTemplateId,
    // 削除確認ポップアップ
    showDeleteConfirmPopup,
    pendingDeleteTemplateId,
    // Handlers
    handleTemplateChange,
    handleNewTemplateFileChange,
    resetNewTemplateForm,
    handleSaveNewTemplate,
    handleRequestDeleteTemplate,
    handleConfirmDeleteTemplate,
    handleCancelDeleteTemplate,
    handleOpenEditTemplatePopup,
    handleUpdateTemplate,
    handleSelectTemplate,
    // 帯操作
    handlePositionChange,
    handleScaleChange,
  };
};
